import nlp from "compromise";
import { createAnchor, getPositionFromIndex } from "./AutoSuggestion";
import { isRejectedByLivePosition, getOverlappingAnchors, isAlreadyAnchored } from "./AutoSuggestion";
import { titleCase } from "title-case";
import { isPositionAnAcceptedSpelling } from "./AutoSuggestion";
export function makeAnchorEmitter({
    texFile,
    session,
    Range,
    anchorlist,
    existingSuggestions,
    staleIds,
    styleName,
    markerClass,
    overlapMode = 'simple',   // 'simple' | 'refresh' | 'skip-if-same'
}) {
    return function emit(
        originalText,
        suggestionText,
        absIndex,
        description,
        extraStyleInfo = {}
    ) {
        // ── 0. Skip if already identical ───────────────────────────────────
        if (originalText === suggestionText) return false;

        // ── 1. Compute row/col position from flat character index ──────────
        // absIndex points to the first character of originalText in texFile.
        // originalText.length drives the end position automatically.
        const position = getPositionFromIndex(texFile, absIndex, originalText.length);
        if (isPositionAnAcceptedSpelling(position, originalText)) {
            return false;
        }

        // ── 2. Rejected guard ──────────────────────────────────────────────
        if (isRejectedByLivePosition(position, styleName, originalText)) return false;

        // ── 3. Overlap handling (3 variants) ──────────────────────────────
        const overlapping = getOverlappingAnchors(position, styleName, existingSuggestions);

        if (overlapping.length > 0) {
            if (overlapMode === 'refresh') {
                // ── "refresh" variant ──────────────────────────────────────
                // Same text at same position → refresh the Ace marker if it
                // was lost (e.g. after an undo), don't mark stale.
                // Different text at same position → mark stale.
                overlapping.forEach(old => {
                    if (old.originalText === originalText) {
                        if (!session.getMarkers()[old.markerId]) {
                            old.markerId = session.addMarker(
                                new Range(
                                    position.startRow, position.startCol,
                                    position.endRow, position.endCol
                                ),
                                markerClass, 'text'
                            );
                        }
                    } else {
                        staleIds.add(old.id);
                    }
                });
                // If every overlapping anchor already has the same text → skip
                if (overlapping.every(old => old.originalText === originalText)) return false;

            } else if (overlapMode === 'skip-if-same') {
                // ── "skip-if-same" variant ─────────────────────────────────
                // Mark as stale if different text, skip if same text already anchored.
                overlapping.forEach(old => {
                    if (old.originalText !== originalText) staleIds.add(old.id);
                });
                if (overlapping.every(old => old.originalText === originalText)) return false;

            } else {
                // ── "simple" variant (default) ─────────────────────────────
                // All overlapping anchors become stale — will be replaced.
                overlapping.forEach(old => staleIds.add(old.id));
            }
        } else {
            // ── 4. Already-anchored guard (no overlaps) ────────────────────
            if (isAlreadyAnchored(position, styleName, existingSuggestions)) return false;
        }

        // ── 5. Comment guard ───────────────────────────────────────────────
        if (typeof findCommandorNot === 'function' &&
            findCommandorNot(position.startRow, position.startCol, texFile)) {
            return false;
        }

        if (isInsideAQTag(position.startRow, position.startCol, texFile)) {
            console.log("Skipping suggestion: Inside \\AQ tag");
            return false;
        }

        // ── 6. Create the anchor ───────────────────────────────────────────
        createAnchor(
            session, Range, position,
            originalText, suggestionText,
            markerClass, styleName,
            description, anchorlist,
            extraStyleInfo   // passed straight through — any shape is supported
        );

        return true;
    };
}

export function isInsideAQTag(row, column, file) {
    const lines = file.split("\n");
    const currentRow = lines[row];

    // If the row doesn't exist or column is at the start, it's not inside a tag
    if (!currentRow || column === 0) return false;

    // We only care about the text on this line before the anchor
    const textBeforeAnchor = currentRow.substring(0, column);

    // Logic: Look for the last occurrence of "\AQ{" 
    // and check if there is a closing "}" after that occurrence.
    const lastAQOpen = textBeforeAnchor.lastIndexOf("\\AQ{");

    if (lastAQOpen === -1) {
        return false; // No \AQ{ found on this line before the anchor
    }

    // Check if the \AQ{ we found was already closed before the anchor column
    const textAfterAQOpen = textBeforeAnchor.substring(lastAQOpen + 4);

    // If there's no "}" between the last "\AQ{" and our anchor, we are inside it.
    // Note: This simple check assumes no nested curly braces inside \AQ.
    if (!textAfterAQOpen.includes("}")) {
        return true;
    }

    return false;
}

/** * Checks if the index is wrapped in math delimiters 
 */
export function isInsideMath(fullText, index) {
    const before = fullText.substring(0, index);

    // Inline $ parity check
    const dollarCount = (before.match(/(?<!\\)\$/g) || []).length;
    if (dollarCount % 2 !== 0) return true;

    // Display math \[ \] check
    const lastOpenDisplay = before.lastIndexOf('\\[');
    const lastCloseDisplay = before.lastIndexOf('\\]');
    if (lastOpenDisplay > lastCloseDisplay) return true;

    // Environment block check
    const lastBeginEq = before.lastIndexOf('\\begin{equation}');
    const lastEndEq = before.lastIndexOf('\\end{equation}');
    if (lastBeginEq > lastEndEq) return true;

    return false;
}

/** * Checks if the hyphen is inside braces that belong to a command like \cmidrule 
 */
function isInsideCommandBraces(fullText, index) {
    const before = fullText.substring(0, index);
    const lastOpenBrace = before.lastIndexOf('{');
    const lastCloseBrace = before.lastIndexOf('}');

    if (lastOpenBrace > lastCloseBrace) {
        const textBeforeBrace = before.substring(0, lastOpenBrace).trim();
        // Matches commands like \cmidrule(r){ or \multicolumn{
        if (/\\\w+(\(.*\))?$/.test(textBeforeBrace)) {
            return true;
        }
    }
    return false;
}
export function parseTabularBody(body, bodyAbsStart) {
    // ── Step 1: split body into rows tracking absolute offsets ──────────────
    // Walk character-by-character so we never lose position.
    // Skip \hline anywhere it appears (before, after, or between rows).
    const rows = [];
    let i = 0;

    while (i < body.length) {
        // Skip leading whitespace and \hline tokens
        while (i < body.length) {
            // Skip whitespace
            if (/\s/.test(body[i])) { i++; continue; }
            // Skip \hline
            if (body.slice(i, i + 6) === '\\hline') { i += 6; continue; }
            break;
        }
        if (i >= body.length) break;

        // Read one row: collect chars until \\ at brace-depth 0, outside $...$
        const rowAbsStart = i; // absolute index of first char of this row in body
        let rowText = '';
        let depth = 0, mathMode = false;

        while (i < body.length) {
            const ch = body[i];

            if (ch === '{') { depth++; rowText += ch; i++; continue; }
            if (ch === '}') { depth--; rowText += ch; i++; continue; }
            if (ch === '$') { mathMode = !mathMode; rowText += ch; i++; continue; }

            // Row terminator: \\ at depth 0 outside math
            if (depth === 0 && !mathMode && body[i] === '\\' && body[i + 1] === '\\') {
                i += 2; // consume \\
                break;
            }

            rowText += ch;
            i++;
        }

        if (rowText.trim().length > 0) {
            rows.push({ text: rowText, rowAbsStart });
        }
    }

    // ── Step 2: parse each row into cells with precise absIdx ───────────────
    function parseCells(row) {
        const cells = [];
        const text = row.text;
        let ci = 0;           // current index within text
        let depth = 0, mathMode = false;
        let cellStart = 0;    // index within text where this cell started

        while (ci <= text.length) {
            const ch = text[ci];
            const atEnd = ci === text.length;
            const isSep = ch === '&' && depth === 0 && !mathMode;

            if (atEnd || isSep) {
                // Slice out the raw cell text (may have leading/trailing spaces)
                const rawCell = text.slice(cellStart, ci);

                // Find first non-whitespace position within rawCell
                const leadingWS = rawCell.search(/\S/);
                // Find last non-whitespace position
                const trailingEnd = rawCell.trimEnd().length;

                const cellText = leadingWS === -1 ? '' : rawCell.slice(leadingWS, trailingEnd);
                const cellAbsIdx = leadingWS === -1
                    ? bodyAbsStart + row.rowAbsStart + cellStart  // empty cell — point to start
                    : bodyAbsStart + row.rowAbsStart + cellStart + leadingWS;

                cells.push({ text: cellText, absIdx: cellAbsIdx, colIndex: cells.length });
                cellStart = ci + 1; // skip the '&'
            } else {
                if (ch === '{') depth++;
                else if (ch === '}') depth--;
                else if (ch === '$') mathMode = !mathMode;
            }
            ci++;
        }
        return cells;
    }

    if (rows.length === 0) return { headerCells: [], contentRows: [] };

    const allParsedRows = rows.map(row => parseCells(row));
    const headerCells = allParsedRows[0] || [];
    const contentRows = allParsedRows.slice(1);

    return { headerCells, contentRows };
}


export function getMathRanges(texFile) {
    const ranges = [];
    // Display math environments
    const envPat = /\\begin\{(equation|align|eqnarray|gather|multline|math|displaymath)\*?\}([\s\S]*?)\\end\{\1\*?\}/g;
    let m;
    while ((m = envPat.exec(texFile)) !== null) {
        ranges.push({
            start: m.index,
            end: m.index + m[0].length,
            fullMatch: m[0],
            content: m[2],
            contentStart: m.index + m[0].indexOf(m[2]),
            type: 'display',
        });
    }
    // Inline math $...$  (skip $$)
    const inlinePat = /(?<!\$)\$(?!\$)([^$]+?)\$(?!\$)/g;
    while ((m = inlinePat.exec(texFile)) !== null) {
        ranges.push({
            start: m.index,
            end: m.index + m[0].length,
            fullMatch: m[0],
            content: m[1],
            contentStart: m.index + 1,
            type: 'inline',
        });
    }
    // Display math \[...\]
    const bracketPat = /\\\[([\s\S]*?)\\\]/g;
    while ((m = bracketPat.exec(texFile)) !== null) {
        ranges.push({
            start: m.index,
            end: m.index + m[0].length,
            fullMatch: m[0],
            content: m[1],
            contentStart: m.index + 2,
            type: 'display',
        });
    }
    return ranges.sort((a, b) => a.start - b.start);
}
export function numberToWords(num) {
    if (num === 0) return "zero";

    const ones = ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];
    const teens = ["ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen"];
    const tens = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];
    const scales = ["", "thousand", "million", "billion", "trillion"];

    function convertGroup(n) {
        let str = "";
        if (n >= 100) {
            str += ones[Math.floor(n / 100)] + " hundred ";
            n %= 100;
        }
        if (n >= 10 && n <= 19) {
            str += teens[n - 10];
        } else if (n >= 20) {
            str += tens[Math.floor(n / 10)] + (n % 10 !== 0 ? "-" + ones[n % 10] : "");
        } else if (n > 0) {
            str += ones[n];
        }
        return str.trim();
    }

    let wordResult = "";
    let scaleIndex = 0;

    while (num > 0) {
        let group = num % 1000;
        if (group !== 0) {
            let groupText = convertGroup(group);
            wordResult = groupText + (scales[scaleIndex] ? " " + scales[scaleIndex] : "") + (wordResult ? " " + wordResult : "");
        }
        num = Math.floor(num / 1000);
        scaleIndex++;
    }

    return wordResult.trim();
}

export function findRowColumn(textFile, index) {
    let row = 0;
    let column = 0;
    for (let i = 0; i < index; i++) {
        if (textFile[i] === "\n") {
            row++;
            column = 0;
        } else {
            column++;
        }
    }
    return {
        row: row,
        column: column
    }
}

export function getLineContent(textFile, index) {
    const lines = textFile.split("\n");
    const rowCol = findRowColumn(textFile, index);
    if (rowCol.row >= lines.length) return "";
    return lines[rowCol.row];
}

export function getLineContentBeforeindex(textFile, index) {
    const lines = textFile.split("\n");
    const rowCol = findRowColumn(textFile, index);
    if (rowCol.row >= lines.length) return "";
    return lines[rowCol.row].slice(0, rowCol.column);
}

export function LineCommented(texFile, index) {
    let lineContentBeforeIndex = getLineContentBeforeindex(texFile, index);

    for (let i = 0; i < lineContentBeforeIndex.length; i++) {
        if (lineContentBeforeIndex[i] === '%') {
            if (i === 0 || lineContentBeforeIndex[i - 1] !== '\\') {
                return true;
            }
        }
    }
    return false;
}




export const removeLines = (editor, startRow, endRow, updateTextFile) => {
    const session = editor.session;
    const Range = window.ace.require('ace/range').Range;

    try {
        const range = new Range(
            startRow,
            0,
            endRow + 1,
            0
        );

        session.remove(range);

        if (updateTextFile) {
            updateTextFile(session.getValue());
        }

        console.log(`Removed lines ${startRow + 1} to ${endRow + 1}`);
        return true;
    } catch (error) {
        console.error("Error removing lines:", error);
        return false;
    }
};


export const shouldRemoveHiddenLine = (editor, item) => {
    const session = editor.session;
    const row = item.startAnchor.row;
    const line = session.getLine(row);
    const percentIndex = line.indexOf('%');
    const hasContentBefore = percentIndex > 0 && line.slice(0, percentIndex).trim().length > 0;

    if (hasContentBefore) {
        return {
            shouldRemove: false,
            removeType: 'partial',
            startCol: percentIndex,
            endCol: line.length
        };
    }
    if (percentIndex === 0 || line.slice(0, percentIndex).trim().length === 0) {
        const prevRow = row - 1;
        const nextRow = row + 1;
        const shouldRemoveLine = true;
        return {
            shouldRemove: shouldRemoveLine,
            removeType: 'full',
            row: row
        };
    }

    return {
        shouldRemove: false,
        removeType: 'none'
    };
};


export const findTabularEnvs = (text) => {
    const results = [];
    const startRegex = /\\begin\{(tabular|longtable|array)\}(\[[^\]]*\])?\{/g;
    let startMatch;
    while ((startMatch = startRegex.exec(text)) !== null) {
        const envName = startMatch[1];
        const optArg = startMatch[2] || '';
        const colSpecStart = startMatch.index + startMatch[0].length;
        let braceCount = 1;
        let i = colSpecStart;
        while (i < text.length && braceCount > 0) {
            if (text[i] === '{') braceCount++;
            else if (text[i] === '}') braceCount--;
            i++;
        }
        if (braceCount !== 0) continue;
        const columnSpec = text.slice(colSpecStart, i - 1);
        const bodyStart = i;
        const endPattern = new RegExp(`\\\\end\\{${envName}\\*?\\}`);
        const endMatch = endPattern.exec(text.slice(bodyStart));
        if (!endMatch) continue;
        const bodyEnd = bodyStart + endMatch.index;
        const tableBody = text.slice(bodyStart, bodyEnd);
        const fullMatch = text.slice(startMatch.index, bodyEnd + endMatch[0].length);
        results.push({ start: startMatch.index, fullMatch, envName, columnSpec, tableBody });
    }
    return results;
};
// ==================== HELPER: Parse column spec ====================
export const parseColumnSpec = (spec) => {
    const parts = [];
    let i = 0;
    while (i < spec.length) {
        const ch = spec[i];
        if (ch === '|' || ch === ' ') {
            parts.push({ char: ch, isDataCol: false });
            i++;
            continue;
        }
        if (ch === '@' || ch === '!' || ch === '>' || ch === '<') {
            let block = ch;
            i++;
            if (i < spec.length && spec[i] === '{') {
                block += '{';
                let bc = 1;
                i++;
                while (i < spec.length && bc > 0) {
                    block += spec[i];
                    if (spec[i] === '{') bc++;
                    else if (spec[i] === '}') bc--;
                    i++;
                }
            }
            parts.push({ char: block, isDataCol: false });
            continue;
        }
        if (ch === 'p' || ch === 'm' || ch === 'b') {
            let block = ch;
            i++;
            if (i < spec.length && spec[i] === '{') {
                block += '{';
                let bc = 1;
                i++;
                while (i < spec.length && bc > 0) {
                    block += spec[i];
                    if (spec[i] === '{') bc++;
                    else if (spec[i] === '}') bc--;
                    i++;
                }
            }
            parts.push({ char: block, isDataCol: true, align: ch });
            continue;
        }
        if (ch === '*') {
            i++;
            if (i < spec.length && spec[i] === '{') {
                i++;
                let numStr = '';
                while (i < spec.length && spec[i] !== '}') {
                    numStr += spec[i];
                    i++;
                }
                i++;
                const repeatCount = parseInt(numStr, 10) || 1;

                if (i < spec.length && spec[i] === '{') {
                    i++;
                    let innerSpec = '';
                    let bc = 1;
                    while (i < spec.length && bc > 0) {
                        if (spec[i] === '{') bc++;
                        else if (spec[i] === '}') bc--;
                        if (bc > 0) innerSpec += spec[i];
                        i++;
                    }
                    for (let r = 0; r < repeatCount; r++) {
                        parts.push({ char: innerSpec, isDataCol: true, align: innerSpec });
                    }
                }
            }
            continue;
        }
        if (/[lcrSXRLCDQ]/.test(ch)) {
            parts.push({ char: ch, isDataCol: true, align: ch });
            i++;
            continue;
        }
        parts.push({ char: ch, isDataCol: false });
        i++;
    }
    return parts;
};


export function findCommandorNot(row, column, file) {
    let lines = file.split("\n");
    let rows = lines[row];
    for (let i = 0; i < column; i++) {
        if (rows[i] === "%" && (i === 0 || rows[i - 1] !== "\\")) {
            return true;
        }
    }
    return false;
}


// export function capitalizeInsideBraces(str, isTitleCase = true) {
//     const skipWords = [
//         "using",
//         // Articles
//         "a", "an", "the",

//         // Conjunctions
//         "and", "but", "or", "nor", "for", "so", "yet", "although", "because", "since", "unless", "whereas", "while", "although", "though",

//         // Prepositions (short + common)
//         "about", "above", "across", "after", "against", "along", "among", "around", "at", "before", "behind", "below", "beneath", "beside", "between", "beyond", "by", "despite", "down", "during", "except", "for", "from", "in", "inside", "into", "like", "near", "of", "off", "on", "onto", "out", "outside", "over", "past", "since", "through", "throughout", "till", "to", "toward", "towards", "under", "underneath", "until", "up", "upon", "with", "within", "without", "via", "per", "as", "than",

//         // Pronouns (common)
//         "i", "you", "he", "she", "it", "we", "they", "me", "him", "her", "us", "them", "my", "your", "his", "her", "its", "our", "their", "mine", "yours", "hers", "ours", "theirs", "this", "that", "these", "those", "any", "each", "few", "many", "all", "some", "most", "none", "one", "other", "another", "such", "what", "which", "who", "whom", "whose", "myself", "yourself", "himself", "herself", "itself", "ourselves", "themselves", "eachother",

//         // Adverbs / common small words
//         "even", "more", "also", "not", "just", "only", "still", "yet", "too", "very", "so", "ever", "never", "once", "always", "often", "sometimes", "usually", "rarely", "perhaps", "maybe", "actually", "indeed", "thus", "there", "here", "where", "when", "why", "how", "however", "instead", "meanwhile", "therefore", "furthermore", "additionally", "likewise", "similarly", "otherwise", "hence", "accordingly", "anyway", "anyhow", "already", "soon", "rather", "quite", "rather", "nearly", "hardly", "merely", "mostly", "mainly", "primarily", "largely", "partly", "somewhat", "relatively", "approximately", "nevertheless", "nonetheless", "besides", "aside", "henceforth", "forthwith", "altogether", "over", "under", "about", "around", "through", "throughout", "beyond", "toward", "towards", "without", "within",

//         // Common filler / linking words
//         "as", "if", "than", "until", "while", "so", "too", "like", "per", "via", "etc", "i.e", "e.g", "vs", "www", "com", "org", "net", "gov", "edu", "mil", "inc", "ltd", "co",

//         // Modifiers / small adjectives
//         "big", "small", "new", "old", "young", "other", "same", "main", "own", "first", "last", "next", "previous", "former", "latter", "only", "several", "many", "few", "some", "all", "any", "enough", "less", "more", "most", "certain", "certainly", "particular", "partly", "whole", "wholeheartedly",

//         // Common adverbs used in captions
//         "quickly", "slowly", "carefully", "easily", "hardly", "barely", "strongly", "lightly", "simply", "clearly", "visibly", "directly", "immediately", "gradually", "mainly", "primarily", "mainly", "slightly", "relatively", "partially", "usually", "commonly", "occasionally", "frequently", "rarely", "occasionally", "effectively", "accurately", "specifically", "generally", "typically", "mostly", "usually", "particularly", "especially", "sometimes", "eventually", "finally", "ultimately",

//         // Time / quantity words
//         "before", "after", "since", "until", "during", "recently", "formerly", "once", "twice", "thrice", "daily", "weekly", "monthly", "yearly", "hourly", "later", "earlier", "soon", "now", "then", "again", "already", "yet", "immediately", "eventually", "finally", "ultimately", "early", "late", "today", "yesterday", "tomorrow", "morning", "evening", "night", "afternoon", "midday", "midnight", "noon", "past", "next", "previous", "current", "following", "upcoming",

//         // Misc / others
//         "example", "examples", "fig", "figure", "table", "eq", "equation", "ref", "refs", "reference", "references", "section", "sections", "chapter", "chapters", "appendix", "appendices", "note", "notes", "supplementary", "supplement", "supplemental", "data", "dataset", "datasets", "sample", "samples", "value", "values", "parameter", "parameters", "variable", "variables", "result", "results", "observation", "observations", "method", "methods", "procedure", "procedures", "analysis", "analyses", "study", "studies", "experiment", "experiments", "test", "tests", "control", "controls", "treatment", "treatments", "group", "groups", "case", "cases", "item", "items", "part", "parts", "component", "components", "factor", "factors", "level", "levels", "type", "types", "category", "categories", "set", "sets", "range", "ranges", "line", "lines", "point", "points", "mean", "median", "mode", "variance", "standard", "deviation", "ratio", "ratios", "percentage", "percentages", "score", "scores", "scorecard", "sampled", "sampleset", "trial", "trials", "phase", "phases",

//         // Common file extensions
//         "jpg", "jpeg", "png", "gif", "bmp", "tif", "tiff", "svg", "webp", "ico", "pdf", "txt", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "csv", "json", "xml", "html", "htm", "css", "js", "ts", "py", "cpp", "c", "h", "java", "rb", "php", "sh", "bat", "exe", "dll", "zip", "tar", "gz", "rar", "7z",

//         // URLs / domain names
//         "com", "org", "net", "gov", "edu", "mil", "io", "co", "info", "biz", "us", "uk", "de", "fr", "jp", "cn", "ru", "in", "au", "br", "ca", "es", "it", "nl", "se", "no", "fi", "dk", "ch", "be", "nz",

//         // Common tech / acronyms often in captions
//         "cpu", "gpu", "ram", "ssd", "hdd", "os", "ui", "ux", "api", "html", "css", "json", "xml", "sql", "db", "dns", "ip", "url", "tcp", "udp", "http", "https", "ftp", "smtp", "ssh", "tls", "ssl", "vpn", "vm", "cli", "gui", "ide", "sdk", "api", "iot", "ai", "ml", "dl", "nn", "cnn", "rnn", "lstm", "gan", "url", "uri", "jpg", "png", "gif", "bmp"
//     ];


//     let result = "";
//     let i = 0;
//     let isFirstWord = true; // Track if this is the first word in the string
//     let afterPeriod = false; // Track if we're after a period

//     while (i < str.length) {
//         // ==================== Handle Math $...$ ====================
//         if (str[i] === '$' && (i === 0 || str[i - 1] !== '\\')) {
//             result += '$';
//             i++;
//             while (i < str.length) {
//                 if (str[i] === '$' && str[i - 1] !== '\\') {
//                     result += '$';
//                     i++;
//                     break;
//                 }
//                 result += str[i];
//                 i++;
//             }
//             continue;
//         }

//         // ==================== Handle LaTeX Commands \\command{} ====================
//         if (str[i] === '\\' && i + 1 < str.length && /[a-zA-Z]/.test(str[i + 1])) {
//             result += '\\';
//             i++;

//             // Read command name (preserve case)
//             while (i < str.length && /[a-zA-Z]/.test(str[i])) {
//                 result += str[i];
//                 i++;
//             }

//             // Skip whitespace after command
//             while (i < str.length && /\s/.test(str[i])) {
//                 result += str[i];
//                 i++;
//             }

//             // Handle braces after command
//             if (i < str.length && str[i] === '{') {
//                 result += '{';
//                 i++;
//                 let braceContent = "";
//                 let braceCount = 1;

//                 while (i < str.length && braceCount > 0) {
//                     if (str[i] === '{' && str[i - 1] !== '\\') {
//                         braceCount++;
//                     } else if (str[i] === '}' && str[i - 1] !== '\\') {
//                         braceCount--;
//                         if (braceCount === 0) break;
//                     }
//                     braceContent += str[i];
//                     i++;
//                 }

//                 // Recursively process content inside command braces
//                 const processedContent = capitalizeInsideBraces(braceContent, isTitleCase);
//                 result += processedContent + '}';
//                 i++;
//             }
//             continue;
//         }

//         // ==================== Handle Words ====================
//         if (/[a-zA-Z]/.test(str[i])) {
//             let word = "";

//             // Collect the word
//             while (i < str.length && /[a-zA-Z0-9]/.test(str[i])) {
//                 word += str[i];
//                 i++;
//             }

//             const lowerWord = word.toLowerCase();

//             // Determine if we should capitalize
//             if (isFirstWord || afterPeriod) {
//                 // Always capitalize first word or word after period
//                 result += word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
//                 isFirstWord = false;
//                 afterPeriod = false;
//             } else if (isTitleCase && skipWords.includes(lowerWord)) {
//                 // Title case: skip small words (unless after period)
//                 result += lowerWord;
//             } else if (isTitleCase) {
//                 // Title case: capitalize other words
//                 result += word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
//             } else {
//                 // Sentence case: lowercase everything except first word
//                 result += lowerWord;
//             }

//             continue;
//         }

//         // ==================== Handle Period ====================
//         if (str[i] === '.') {
//             result += str[i];
//             afterPeriod = true; // Next word should be capitalized
//             i++;
//             continue;
//         }

//         // ==================== Handle Opening Brace { ====================
//         if (str[i] === '{') {
//             result += str[i];
//             i++;
//             isFirstWord = true; // Next word after { should be capitalized
//             continue;
//         }

//         // ==================== Handle Other Characters ====================
//         result += str[i];
//         i++;
//     }

//     return result;
// }






// export function capitalizeInsideBraces(str, isTitleCase = true) {
//     const skipWords = [
//         "using",
//         // Articles
//         "a", "an", "the",

//         // Conjunctions
//         "and", "but", "or", "nor", "for", "so", "yet", "although", "because", "since", "unless", "whereas", "while", "although", "though",

//         // Prepositions (short + common)
//         "about", "above", "across", "after", "against", "along", "among", "around", "at", "before", "behind", "below", "beneath", "beside", "between", "beyond", "by", "despite", "down", "during", "except", "for", "from", "in", "inside", "into", "like", "near", "of", "off", "on", "onto", "out", "outside", "over", "past", "since", "through", "throughout", "till", "to", "toward", "towards", "under", "underneath", "until", "up", "upon", "with", "within", "without", "via", "per", "as", "than",

//         // Pronouns (common)
//         "i", "you", "he", "she", "it", "we", "they", "me", "him", "her", "us", "them", "my", "your", "his", "her", "its", "our", "their", "mine", "yours", "hers", "ours", "theirs", "this", "that", "these", "those", "any", "each", "few", "many", "all", "some", "most", "none", "one", "other", "another", "such", "what", "which", "who", "whom", "whose", "myself", "yourself", "himself", "herself", "itself", "ourselves", "themselves", "eachother",

//         // Adverbs / common small words
//         "even", "more", "also", "not", "just", "only", "still", "yet", "too", "very", "so", "ever", "never", "once", "always", "often", "sometimes", "usually", "rarely", "perhaps", "maybe", "actually", "indeed", "thus", "there", "here", "where", "when", "why", "how", "however", "instead", "meanwhile", "therefore", "furthermore", "additionally", "likewise", "similarly", "otherwise", "hence", "accordingly", "anyway", "anyhow", "already", "soon", "rather", "quite", "rather", "nearly", "hardly", "merely", "mostly", "mainly", "primarily", "largely", "partly", "somewhat", "relatively", "approximately", "nevertheless", "nonetheless", "besides", "aside", "henceforth", "forthwith", "altogether", "over", "under", "about", "around", "through", "throughout", "beyond", "toward", "towards", "without", "within",

//         // Common filler / linking words
//         "as", "if", "than", "until", "while", "so", "too", "like", "per", "via", "etc", "i.e", "e.g", "vs", "www", "com", "org", "net", "gov", "edu", "mil", "inc", "ltd", "co",

//         // Modifiers / small adjectives
//         "big", "small", "new", "old", "young", "other", "same", "main", "own", "first", "last", "next", "previous", "former", "latter", "only", "several", "many", "few", "some", "all", "any", "enough", "less", "more", "most", "certain", "certainly", "particular", "partly", "whole", "wholeheartedly",

//         // Common adverbs used in captions
//         "quickly", "slowly", "carefully", "easily", "hardly", "barely", "strongly", "lightly", "simply", "clearly", "visibly", "directly", "immediately", "gradually", "mainly", "primarily", "mainly", "slightly", "relatively", "partially", "usually", "commonly", "occasionally", "frequently", "rarely", "occasionally", "effectively", "accurately", "specifically", "generally", "typically", "mostly", "usually", "particularly", "especially", "sometimes", "eventually", "finally", "ultimately",

//         // Time / quantity words
//         "before", "after", "since", "until", "during", "recently", "formerly", "once", "twice", "thrice", "daily", "weekly", "monthly", "yearly", "hourly", "later", "earlier", "soon", "now", "then", "again", "already", "yet", "immediately", "eventually", "finally", "ultimately", "early", "late", "today", "yesterday", "tomorrow", "morning", "evening", "night", "afternoon", "midday", "midnight", "noon", "past", "next", "previous", "current", "following", "upcoming",

//         // Misc / others
//         "example", "examples", "fig", "figure", "table", "eq", "equation", "ref", "refs", "reference", "references", "section", "sections", "chapter", "chapters", "appendix", "appendices", "note", "notes", "supplementary", "supplement", "supplemental", "data", "dataset", "datasets", "sample", "samples", "value", "values", "parameter", "parameters", "variable", "variables", "result", "results", "observation", "observations", "method", "methods", "procedure", "procedures", "analysis", "analyses", "study", "studies", "experiment", "experiments", "test", "tests", "control", "controls", "treatment", "treatments", "group", "groups", "case", "cases", "item", "items", "part", "parts", "component", "components", "factor", "factors", "level", "levels", "type", "types", "category", "categories", "set", "sets", "range", "ranges", "line", "lines", "point", "points", "mean", "median", "mode", "variance", "standard", "deviation", "ratio", "ratios", "percentage", "percentages", "score", "scores", "scorecard", "sampled", "sampleset", "trial", "trials", "phase", "phases",

//         // Common file extensions
//         "jpg", "jpeg", "png", "gif", "bmp", "tif", "tiff", "svg", "webp", "ico", "pdf", "txt", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "csv", "json", "xml", "html", "htm", "css", "js", "ts", "py", "cpp", "c", "h", "java", "rb", "php", "sh", "bat", "exe", "dll", "zip", "tar", "gz", "rar", "7z",

//         // URLs / domain names
//         "com", "org", "net", "gov", "edu", "mil", "io", "co", "info", "biz", "us", "uk", "de", "fr", "jp", "cn", "ru", "in", "au", "br", "ca", "es", "it", "nl", "se", "no", "fi", "dk", "ch", "be", "nz",

//         // Common tech / acronyms often in captions
//         "cpu", "gpu", "ram", "ssd", "hdd", "os", "ui", "ux", "api", "html", "css", "json", "xml", "sql", "db", "dns", "ip", "url", "tcp", "udp", "http", "https", "ftp", "smtp", "ssh", "tls", "ssl", "vpn", "vm", "cli", "gui", "ide", "sdk", "api", "iot", "ai", "ml", "dl", "nn", "cnn", "rnn", "lstm", "gan", "url", "uri", "jpg", "png", "gif", "bmp"
//     ];
//     let result = "";
//     let i = 0;
//     let isFirstWord = true;
//     let afterPeriod = false;

//     // Commands whose contents should NEVER be capitalized/modified
//     const protectedCommands = ["mth", "chem", "ref", "cite", "label"];

//     while (i < str.length) {
//         // ==================== 1. Handle Math $...$ ====================
//         if (str[i] === '$' && (i === 0 || str[i - 1] !== '\\')) {
//             let mathBlock = "$";
//             i++;
//             while (i < str.length) {
//                 mathBlock += str[i];
//                 if (str[i] === '$' && str[i - 1] !== '\\') { i++; break; }
//                 i++;
//             }
//             result += mathBlock;
//             continue;
//         }

//         // ==================== 2. Handle LaTeX Commands \\command{} ====================
//         if (str[i] === '\\' && i + 1 < str.length && /[a-zA-Z]/.test(str[i + 1])) {
//             result += '\\';
//             i++;
//             let cmdName = "";
//             while (i < str.length && /[a-zA-Z]/.test(str[i])) {
//                 cmdName += str[i];
//                 result += str[i];
//                 i++;
//             }

//             // Skip whitespace
//             while (i < str.length && /\s/.test(str[i])) { result += str[i]; i++; }

//             if (i < str.length && str[i] === '{') {
//                 result += '{';
//                 i++;
//                 let braceContent = "";
//                 let braceCount = 1;
//                 while (i < str.length && braceCount > 0) {
//                     if (str[i] === '{' && str[i - 1] !== '\\') braceCount++;
//                     else if (str[i] === '}' && str[i - 1] !== '\\') {
//                         braceCount--;
//                         if (braceCount === 0) break;
//                     }
//                     braceContent += str[i];
//                     i++;
//                 }

//                 // CHECK: Is this a protected command?
//                 if (protectedCommands.includes(cmdName.toLowerCase())) {
//                     result += braceContent + '}'; // Keep exactly as is
//                 } else {
//                     result += capitalizeInsideBraces(braceContent, isTitleCase) + '}';
//                 }
//                 i++;
//             }
//             continue;
//         }

//         // ==================== 3. Handle Words & Chem (H$_2$O) ====================
//         // We modify the word detection to include $, _, and { } if they are part of a chemical string
//         if (/[a-zA-Z]/.test(str[i])) {
//             let word = "";
//             let hasSpecialChemChar = false;

//             // Collect word, but allow $, _, {, } inside it to capture H$_2$O or H_{2}O
//             while (i < str.length && /[a-zA-Z0-9$_\{\}]/.test(str[i])) {
//                 if (/[$_\{\}]/.test(str[i])) hasSpecialChemChar = true;
//                 word += str[i];
//                 i++;
//                 // If we hit a space or punctuation not related to chem, stop
//                 if (i < str.length && /[\s,.?!:;]/.test(str[i])) break;
//             }

//             const lowerWord = word.toLowerCase();

//             // If it contains $ or _, it's likely chemistry or math-text (H$_2$O)
//             // We should leave these exactly as the user wrote them.
//             if (hasSpecialChemChar) {
//                 result += word;
//             } else if (isFirstWord || afterPeriod) {
//                 result += word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
//                 isFirstWord = false;
//                 afterPeriod = false;
//             } else if (isTitleCase && skipWords.includes(lowerWord)) {
//                 result += lowerWord;
//             } else if (isTitleCase) {
//                 result += word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
//             } else {
//                 result += lowerWord;
//             }
//             continue;
//         }

//         // Handle Punctuation/Braces (Same as your current logic)
//         if (str[i] === '.') { result += '.'; afterPeriod = true; i++; continue; }
//         if (str[i] === '{') { result += '{'; i++; isFirstWord = true; continue; }

//         result += str[i];
//         i++;
//     }
//     return result;
// }

export function capitalizeInsideBraces(str, isTitleCase = true) {

    // Commands whose brace contents must NEVER be touched
    const PROTECTED = new Set(["mth", "chem", "ref", "cite", "label"]);

    // ── Sentence case path (simpler — no library needed) ─────────────────────
    if (!isTitleCase) {
        return _sentenceCase(str, PROTECTED);
    }

    // ── Title Case path — placeholder strategy ────────────────────────────────
    //
    // Problem: titleCase() needs the WHOLE phrase at once to know whether a word
    // like "for" is mid-sentence (lowercase) or sentence-initial (Title Case).
    // If we process plain-text segments individually after LaTeX tokens, the
    // library loses context and wrongly capitalises words like "for", "in", "of".
    //
    // Solution:
    //   1. Walk the string once, replacing each LaTeX token with a unique
    //      lowercase alphabetic placeholder (e.g. "zztok0", "zztok1").
    //      These look like ordinary words to the library.
    //   2. Lowercase the entire masked string (handles ALL CAPS input).
    //   3. Call titleCase() on the masked string — it now has full phrase context.
    //   4. Walk the token list and replace each placeholder with the original
    //      token (recursing into non-protected command contents).

    const tokens = [];          // recorded tokens in order
    let masked = "";          // the string with LaTeX replaced by placeholders
    let idx = 0;           // walk index
    let pNum = 0;           // placeholder counter

    function nextPlaceholder() {
        return `zztok${pNum++}`;    // unique, lowercase, alphabetic — title-case sees it as a word
    }

    function extractBracedContent(s, openBrace) {
        // openBrace = index of '{' in s
        let depth = 1, j = openBrace + 1, content = '';
        while (j < s.length && depth > 0) {
            if (s[j] === '{') depth++;
            else if (s[j] === '}') { depth--; if (depth === 0) break; }
            content += s[j]; j++;
        }
        return { content, closeIdx: j };
    }

    while (idx < str.length) {
        // ── Math $...$ ────────────────────────────────────────────────────────
        if (str[idx] === '$') {
            let blk = "$"; idx++;
            while (idx < str.length) {
                blk += str[idx];
                if (str[idx] === '$') { idx++; break; }
                idx++;
            }
            const p = nextPlaceholder();
            tokens.push({ p, original: blk, recurse: false });
            masked += p;
            continue;
        }

        // ── LaTeX command \cmd{...} ───────────────────────────────────────────
        if (str[idx] === '\\' && idx + 1 < str.length && /[a-zA-Z]/.test(str[idx + 1])) {
            let cmd = "\\"; idx++;
            while (idx < str.length && /[a-zA-Z]/.test(str[idx])) { cmd += str[idx]; idx++; }
            const cmdName = cmd.slice(1);

            let ws = "";
            while (idx < str.length && /\s/.test(str[idx])) { ws += str[idx]; idx++; }

            if (idx < str.length && str[idx] === '{') {
                const { content, closeIdx } = extractBracedContent(str, idx);
                const isProtected = PROTECTED.has(cmdName.toLowerCase());
                const p = nextPlaceholder();
                tokens.push({
                    p,
                    cmdName, ws, content,
                    isProtected,
                    recurse: true,  // will recurse on restore (unless protected)
                });
                masked += p;
                idx = closeIdx + 1;
            } else {
                // Command with no braces (e.g. \LaTeX, \textit )
                const p = nextPlaceholder();
                tokens.push({ p, original: cmd + ws, recurse: false });
                masked += p;
            }
            continue;
        }

        // ── Plain text ────────────────────────────────────────────────────────
        masked += str[idx]; idx++;
    }

    // Apply title-case to the fully masked string.
    // Lowercase first so ALL CAPS input ("RESULTS") is handled correctly.
    const titled = titleCase(masked.toLowerCase());

    // Restore tokens — replace each placeholder (case-insensitively, since
    // titleCase may have capitalised e.g. "Zztok0") with the original content.
    let result = titled;
    for (const tok of tokens) {
        let replacement;
        if (tok.recurse && tok.cmdName !== undefined) {
            // Recurse into the command's brace content (unless protected)
            const inner = tok.isProtected
                ? tok.content
                : capitalizeInsideBraces(tok.content, true);
            replacement = `\\${tok.cmdName}${tok.ws}{${inner}}`;
        } else {
            replacement = tok.original;
        }
        // The placeholder may have been title-cased (e.g. "Zztok0") — match any case
        result = result.replace(new RegExp(tok.p, 'i'), replacement);
    }

    return result;
}


// ── Sentence case helper (private) ────────────────────────────────────────────
function _sentenceCase(str, PROTECTED) {
    let result = "";
    let i = 0;
    let isFirst = true;   // capitalize the very first letter we encounter

    function extractBracedContent(s, openBrace) {
        let depth = 1, j = openBrace + 1, content = '';
        while (j < s.length && depth > 0) {
            if (s[j] === '{') depth++;
            else if (s[j] === '}') { depth--; if (depth === 0) break; }
            content += s[j]; j++;
        }
        return { content, closeIdx: j };
    }

    while (i < str.length) {
        // Math $...$: pass through unchanged
        if (str[i] === '$') {
            let blk = "$"; i++;
            while (i < str.length) { blk += str[i]; if (str[i] === '$') { i++; break; } i++; }
            result += blk; continue;
        }

        // LaTeX command: preserve name, recurse into content (unless protected)
        if (str[i] === '\\' && i + 1 < str.length && /[a-zA-Z]/.test(str[i + 1])) {
            let cmd = "\\"; i++;
            while (i < str.length && /[a-zA-Z]/.test(str[i])) { cmd += str[i]; i++; }
            const cmdName = cmd.slice(1);
            let ws = "";
            while (i < str.length && /\s/.test(str[i])) { ws += str[i]; i++; }
            if (i < str.length && str[i] === '{') {
                const { content, closeIdx } = extractBracedContent(str, i);
                const isProtected = PROTECTED.has(cmdName.toLowerCase());
                // In sentence case, content inside commands is also sentence-cased
                const inner = isProtected ? content : _sentenceCase(content, PROTECTED);
                result += cmd + ws + '{' + inner + '}';
                i = closeIdx + 1;
            } else {
                result += cmd + ws;
            }
            continue;
        }

        // Letter run: apply lowercase or first-letter capitalize
        if (/[a-zA-Z]/.test(str[i])) {
            let word = "";
            while (i < str.length && /[a-zA-Z]/.test(str[i])) { word += str[i]; i++; }
            const lower = word.toLowerCase();
            if (isFirst) {
                result += lower[0].toUpperCase() + lower.slice(1);
                isFirst = false;
            } else {
                result += lower;
            }
            continue;
        }

        // Period: next word is NOT a new sentence start (unlike title case)
        // In sentence case we only capitalize the very first word of the heading.
        // After a period within a heading, we keep lowercase (the heading is one sentence).
        result += str[i]; i++;
    }
    return result;
}
export function isCommented(texFile, matchIndex) {
    const lineStart = texFile.lastIndexOf("\n", matchIndex - 1) + 1;
    const lineContent = texFile.slice(lineStart, matchIndex);

    // Find every % on this line before the match
    // but skip \% (escaped percent = literal percent sign, not a comment)
    for (let i = 0; i < lineContent.length; i++) {
        if (lineContent[i] === "%") {
            // Check if it's escaped — count backslashes before it
            let backslashCount = 0;
            let j = i - 1;
            while (j >= 0 && lineContent[j] === "\\") {
                backslashCount++;
                j--;
            }
            // Odd number of backslashes = escaped \% → not a comment
            // Even number (including 0) = real % → is a comment
            if (backslashCount % 2 === 0) {
                return true;  // real comment found before the match
            }
        }
    }
    return false;
}
export function getallData(file, startIndex) {
    const result = {
        text: "",
        squaretext: false,
        unnumbered: false,
        unnumberedbracket: false,
        fullMatch: "",
        patterns: {
            hasSquareBracket: false,
            hasAsterisk: false
        }
    };

    let i = startIndex;

    // Check for asterisk (*)
    if (file[i] === '*') {
        result.unnumbered = true;
        result.patterns.hasAsterisk = true;
        i++;
    }

    // Skip whitespace
    while (i < file.length && /\s/.test(file[i])) {
        i++;
    }

    // Check for square bracket []
    if (file[i] === '[') {
        result.patterns.hasSquareBracket = true;
        i++; // Skip opening [
        let squareContent = "";
        let bracketDepth = 1;

        while (i < file.length && bracketDepth > 0) {
            if (file[i] === '[') bracketDepth++;
            else if (file[i] === ']') {
                bracketDepth--;
                if (bracketDepth === 0) break;
            }
            squareContent += file[i];
            i++;
        }

        if (file[i] === ']') i++; // Skip closing ]

        if (result.unnumbered) {
            result.unnumberedbracket = squareContent;
        } else {
            result.squaretext = squareContent;
        }
    }

    // Skip whitespace
    while (i < file.length && /\s/.test(file[i])) {
        i++;
    }

    // Get content inside curly braces {}
    if (file[i] === '{') {
        const braceContent = getInsideTextofBraces(file.slice(i));
        result.text = braceContent.slice(1, -1); // Remove outer { }
        result.fullMatch = file.slice(startIndex, i + braceContent.length);
    }

    return result;
}

export function getInsideTextofBraces(file) {
    let data = "";
    let brace = 0;

    for (let i = 0; i < file.length; i++) {
        if (file[i] === "{") {
            brace++;
        }
        if (file[i] === "}") {
            brace--;
        }
        data = data + file[i];
        if (brace === 0) {
            break;
        }
    }

    return data;
}
export function createGranularAnchors(session, Range, texFile, fullMatchStart, fullMatchText, originalSpec, newSpec, markerClass, styleName, anchorlist) {
    const changes = [];
    const colSpecPattern = /\\begin\{[^}]+\}\s*(?:\[[^\]]*\])?\s*\{([^}]+)\}/;
    const colSpecMatch = fullMatchText.match(colSpecPattern);

    if (!colSpecMatch) return;

    const colSpecStartInMatch = colSpecMatch.index + colSpecMatch[0].lastIndexOf('{') + 1;
    const colSpecStartInFile = fullMatchStart + colSpecStartInMatch;

    let originalIdx = 0;
    let newIdx = 0;

    while (originalIdx < originalSpec.length || newIdx < newSpec.length) {
        if (originalIdx < originalSpec.length && newIdx < newSpec.length) {
            if (originalSpec[originalIdx] === newSpec[newIdx]) {
                originalIdx++;
                newIdx++;
            } else {
                let originalSegment = originalSpec[originalIdx];
                let newSegment = newSpec[newIdx];

                changes.push({
                    position: colSpecStartInFile + originalIdx,
                    length: originalSegment.length,
                    original: originalSegment,
                    replacement: newSegment,
                    columnIndex: originalIdx
                });

                originalIdx++;
                newIdx++;
            }
        } else if (originalIdx < originalSpec.length) {
            changes.push({
                position: colSpecStartInFile + originalIdx,
                length: 1,
                original: originalSpec[originalIdx],
                replacement: '',
                columnIndex: originalIdx
            });
            originalIdx++;
        } else {
            changes.push({
                position: colSpecStartInFile + originalIdx,
                length: 0,
                original: '',
                replacement: newSpec[newIdx],
                columnIndex: originalIdx
            });
            newIdx++;
        }
    }

    changes.forEach((change) => {
        const position = getPositionFromIndex(texFile, change.position, change.length);

        let description = '';
        if (change.original && change.replacement) {
            description = `Change column alignment: "${change.original}" → "${change.replacement}"`;
        } else if (change.replacement) {
            description = `Insert column alignment: "${change.replacement}"`;
        } else {
            description = `Remove column alignment: "${change.original}"`;
        }
        if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
            createAnchor(
                session,
                Range,
                position,
                change.original,
                change.replacement,
                markerClass,
                styleName,
                description,
                anchorlist
            );
        }
    });

    return changes;
}

// Helper to check if a line is commented
export const isLineCommented = (text, index) => {
    let lineStart = index;
    while (lineStart > 0 && text[lineStart - 1] !== '\n') {
        lineStart--;
    }
    const linePrefix = text.slice(lineStart, index);
    for (let i = 0; i < linePrefix.length; i++) {
        if (linePrefix[i] === '%' && (i === 0 || linePrefix[i - 1] !== '\\')) {
            return true;
        }
    }
    return false;
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 1 — MATH ZONE BUILDERS
// ─────────────────────────────────────────────────────────────────────────────

const MATH_KIND_EXTRACTORS = {
    '$': (t) => {
        const zones = [];
        let inDollar = false, start = -1;
        for (let i = 0; i < t.length; i++) {
            if (t[i] === '\\') { i++; continue; }
            if (t[i] === '$' && t[i + 1] === '$') { i++; continue; }
            if (t[i] === '$') {
                if (!inDollar) { inDollar = true; start = i; }
                else { inDollar = false; zones.push({ start, end: i + 1 }); }
            }
        }
        return zones;
    },
    '$$': (t) => _patZones(t, /\$\$([\s\S]*?)\$\$/g),
    '\\(': (t) => _patZones(t, /\\\(([\s\S]*?)\\\)/g),
    '\\[': (t) => _patZones(t, /\\\[([\s\S]*?)\\\]/g),
};

function _patZones(t, pat) {
    const zones = []; let m;
    pat.lastIndex = 0;
    while ((m = pat.exec(t)) !== null) zones.push({ start: m.index, end: m.index + m[0].length });
    return zones;
}

const MATH_ENVS = ['equation', 'align', 'alignat', 'gather', 'multline', 'eqnarray', 'math', 'displaymath', 'array', 'split'];
for (const env of MATH_ENVS) {
    MATH_KIND_EXTRACTORS[env] = (t) =>
        _patZones(t, new RegExp(`\\\\begin\\{${env}\\*?\\}[\\s\\S]*?\\\\end\\{${env}\\*?\\}`, 'g'));
}

export function getMathZones(texFile, excludeMath) {
    if (!excludeMath) return [];
    const kinds = excludeMath === true ? Object.keys(MATH_KIND_EXTRACTORS) : excludeMath;
    const zones = [];
    for (const k of kinds) {
        const fn = MATH_KIND_EXTRACTORS[k];
        if (!fn) { console.warn(`[Framework] Unknown math kind: "${k}"`); continue; }
        fn(texFile).forEach(z => zones.push(z));
    }
    zones.sort((a, b) => a.start - b.start);
    const merged = [];
    for (const z of zones) {
        if (merged.length && z.start < merged[merged.length - 1].end)
            merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, z.end);
        else merged.push({ ...z });
    }
    return merged;
}

// ─────────────────────────────────────────────────────────────────────────────

// SECTION 2 — SCOPE EXTRACTORS
// ─────────────────────────────────────────────────────────────────────────────

export function extractBracedContent(text, openBraceIdx) {
    let depth = 1, i = openBraceIdx + 1, content = '';
    while (i < text.length && depth > 0) {
        const ch = text[i];
        if (ch === '{') depth++;
        else if (ch === '}') { depth--; if (depth === 0) break; }
        content += ch; i++;
    }
    return { content, closeIdx: i };
}

export function extractCommandScopes(texFile, commandNames) {
    const results = [];
    for (const cmd of commandNames) {
        const pat = new RegExp(`\\\\${cmd}\\s*\\{`, 'g'); let m;
        while ((m = pat.exec(texFile)) !== null) {
            if (LineCommented(texFile, m.index)) continue;
            const ob = m.index + m[0].length - 1;
            const { content, closeIdx } = extractBracedContent(texFile, ob);
            results.push({ text: content, absStart: ob + 1, absEnd: closeIdx });
        }
    }
    return results.sort((a, b) => a.absStart - b.absStart);
}

export function extractEnvironmentScopes(texFile, envNames) {
    const results = [];
    for (const env of envNames) {
        const pat = new RegExp(`\\\\begin\\{${env}\\*?\\}([\\s\\S]*?)\\\\end\\{${env}\\*?\\}`, 'g'); let m;
        while ((m = pat.exec(texFile)) !== null) {
            if (LineCommented(texFile, m.index)) continue;
            const is = m.index + m[0].indexOf(m[1]);
            results.push({ text: m[1], absStart: is, absEnd: is + m[1].length });
        }
    }
    return results.sort((a, b) => a.absStart - b.absStart);
}

export function extractDocumentScope(texFile) {
    const m = /\\begin\{document\}([\s\S]*?)\\end\{document\}/.exec(texFile);
    if (!m) return [];
    const is = m.index + m[0].indexOf(m[1]);
    return [{ text: m[1], absStart: is, absEnd: is + m[1].length }];
}

export function extractInnerCommandScopes(outerScopes, innerCmd) {
    const results = [];
    for (const scope of outerScopes) {
        const pat = new RegExp(`\\\\${innerCmd}\\s*\\{`, 'g'); let m;
        while ((m = pat.exec(scope.text)) !== null) {
            const ob = m.index + m[0].length - 1;
            const { content } = extractBracedContent(scope.text, ob);
            const as = scope.absStart + ob + 1;
            results.push({ text: content, absStart: as, absEnd: as + content.length });
        }
    }
    return results.sort((a, b) => a.absStart - b.absStart);
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 3 — EXCLUSION ZONE BUILDERS
// ─────────────────────────────────────────────────────────────────────────────

export function buildCommandExclusionZones(texFile, cmdNames) {
    const zones = [];
    for (const cmd of cmdNames) {
        const pat = new RegExp(`\\\\${cmd}\\s*\\{`, 'g'); let m;
        while ((m = pat.exec(texFile)) !== null) {
            const { closeIdx } = extractBracedContent(texFile, m.index + m[0].length - 1);
            zones.push({ start: m.index, end: closeIdx + 1 });
        }
    }
    return zones;
}

export function buildEnvExclusionZones(texFile, envNames) {
    const zones = [];
    for (const env of envNames) {
        const pat = new RegExp(`\\\\begin\\{${env}\\*?\\}[\\s\\S]*?\\\\end\\{${env}\\*?\\}`, 'g'); let m;
        while ((m = pat.exec(texFile)) !== null) zones.push({ start: m.index, end: m.index + m[0].length });
    }
    return zones;
}
export function buildAllCommandExclusionZones(text) {
    const zones = [];
    let i = 0;
    while (i < text.length) {
        if (text[i] === '\\') {
            const start = i;
            i++;
            // Must be a letter to count as a command (not \\ newline etc.)
            if (i >= text.length || !/[a-zA-Z*@]/.test(text[i])) continue;
            // Consume command name
            while (i < text.length && /[a-zA-Z*@]/.test(text[i])) i++;
            // Consume all immediately-following argument groups in any order
            while (i < text.length) {
                const c = text[i];
                if (c === '(') {
                    let d = 1; i++;
                    while (i < text.length && d > 0) {
                        if (text[i] === '(') d++;
                        else if (text[i] === ')') d--;
                        i++;
                    }
                } else if (c === '[') {
                    let d = 1; i++;
                    while (i < text.length && d > 0) {
                        if (text[i] === '[') d++;
                        else if (text[i] === ']') d--;
                        i++;
                    }
                } else if (c === '{') {
                    let d = 1; i++;
                    while (i < text.length && d > 0) {
                        if (text[i] === '{') d++;
                        else if (text[i] === '}') d--;
                        i++;
                    }
                } else {
                    break;  // no more argument groups
                }
            }
            zones.push({ start, end: i });
        } else {
            i++;
        }
    }
    return zones;
}

export function splitByExclusions(scope, exclusionZones) {
    const local = exclusionZones
        .map(z => ({
            start: Math.max(0, z.start - scope.absStart),
            end: Math.min(scope.text.length, z.end - scope.absStart),
        }))
        .filter(z => z.start < scope.text.length && z.end > 0 && z.start < z.end)
        .sort((a, b) => a.start - b.start);

    if (local.length === 0) return [{ text: scope.text, absStart: scope.absStart }];

    const segs = [];
    let cursor = 0;

    for (const z of local) {
        // Only emit a segment if there is uncovered text before this zone
        if (z.start > cursor) {
            segs.push({
                text: scope.text.slice(cursor, z.start),
                absStart: scope.absStart + cursor,
            });
        }

        cursor = Math.max(cursor, z.end);   // ← was: cursor = z.end
    }

    if (cursor < scope.text.length) {
        segs.push({
            text: scope.text.slice(cursor),
            absStart: scope.absStart + cursor,
        });
    }

    return segs.filter(s => s.text.length > 0);
}

function filterToOnlyEnvironments(texFile, outerSegments, envNames) {
    const result = [];

    for (const seg of outerSegments) {
        for (const env of envNames) {
            // Match \begin{env} or \begin{env*} inside this segment's text
            const beginPat = new RegExp(
                `\\\\begin\\{${env}\\*?\\}([\\s\\S]*?)\\\\end\\{${env}\\*?\\}`,
                'g'
            );
            let m;
            while ((m = beginPat.exec(seg.text)) !== null) {
                // m[1] is the content between \begin{env} and \end{env}
                const innerText = m[1];
                const innerStart = seg.absStart + m.index + m[0].indexOf(m[1]);
                result.push({
                    text: innerText,
                    absStart: innerStart,
                });
            }
        }
    }

    return result.sort((a, b) => a.absStart - b.absStart);
}
function filterToOnlyMathZones(texFile, outerScopes, excludeMath) {
    const mathZones = getMathZones(texFile, excludeMath === true || excludeMath === false
        ? true   // get all math kinds when onlyMath is true
        : excludeMath
    );

    const result = [];
    for (const scope of outerScopes) {
        for (const z of mathZones) {
            // Find overlap between math zone and outer scope
            const start = Math.max(z.start, scope.absStart);
            const end = Math.min(z.end, scope.absEnd);
            if (start >= end) continue;

            result.push({
                text: texFile.slice(start, end),
                absStart: start,
                absEnd: end,
            });
        }
    }
    return result.sort((a, b) => a.absStart - b.absStart);
}



function buildQueryOnlyEmitter({ texFile, session, anchorlist, existingSuggestions, staleIds, styleName, markerClass, insertedAQSet }) {
    return function rawEmitQuery(originalText, queryMessage, absIndex, description) {

        const position = getPositionFromIndex(texFile, absIndex, originalText.length);

        // Rejected guard
        if (isRejectedByLivePosition(position, styleName, originalText)) return;

        // Skip if \AQ already inserted above this line
        // const row = position.startRow;
        // if (row > 0 && session?.getLine) {
        //     const lineAbove = session.getLine(row - 1) || '';
        //     if (lineAbove.trimStart().startsWith('\\AQ{')) return;
        // }
        // On Ctrl+Z the \AQ{} disappears → next re-run → no match → emits again.
        if (insertedAQSet && insertedAQSet.has(description?.trim())) return;

        // Overlap: rescue identical message, replace changed message
        const overlapping = getOverlappingAnchors(position, styleName, existingSuggestions);
        if (overlapping.length > 0) {
            const identical = overlapping.filter(old => old.styleInfo?.queryMessage === queryMessage);
            if (identical.length > 0) {
                identical.forEach(old => staleIds.delete(old.id));  // RESCUE
                return;
            }
            overlapping.forEach(old => staleIds.add(old.id));       // REPLACE
        } else {
            const already = existingSuggestions.some(ex =>
                ex.styleInfo?.name === styleName &&
                ex.styleInfo?.queryMessage === queryMessage &&
                ex.startAnchor?.row === position.startRow &&
                ex.startAnchor?.column === position.startCol
            );
            if (already) return;
        }

        // Comment guard
        if (findCommandorNot(position.startRow, position.startCol, texFile)) return;

        // Create queryOnly anchor
        const id = `${styleName}__${crypto.randomUUID()}`;
        const startAnchor = session.doc.createAnchor(position.startRow, position.startCol);
        const endAnchor = session.doc.createAnchor(position.endRow, position.endCol);
        startAnchor.$insertRight = false;
        endAnchor.$insertRight = true;

        anchorlist.push({
            id,
            startAnchor,
            endAnchor,
            markerId: null,
            originalText,
            suggestionText: null,
            queryOnly: true,
            styleInfo: {
                name: styleName,
                description,
                queryMessage,
            },
        });
    };
}


export function buildStyleContext(params) {
    const {
        texFile,
        session,
        Range,
        anchorlist,
        existingSuggestions,
        staleIds,
        styleName,
        markerClass,
        scope = {},
        excludeMath = true,
        onlyMath = false,
        excludeInner = [],
        excludeEnvironments = [],
        onlyEnvironments = [],
        excludeOnlyEnvironments = [],
        excludeAllCommands = false,
        mode = 'suggestion',
        overlapMode = 'simple',
    } = params;

    const insertedAQSet = new Set();
    const aqScanPat = /\\AQ\{/g;
    let aqm;
    while ((aqm = aqScanPat.exec(texFile)) !== null) {
        let depth = 1, i = aqm.index + aqm[0].length, content = '';
        while (i < texFile.length && depth > 0) {
            if (texFile[i] === '{') depth++;
            else if (texFile[i] === '}') { depth--; if (depth === 0) break; }
            content += texFile[i++];
        }
        insertedAQSet.add(content.trim());
    }

    // ── Step 1: Resolve outer scope ───────────────────────────────────────────
    let outerScopes;
    if (scope.custom && scope.custom.length > 0) {
        outerScopes = scope.custom.map(s => ({
            text: s.text, absStart: s.absStart, absEnd: s.absStart + s.text.length,
        }));
    } else if (scope.commands && scope.commands.length > 0) {
        outerScopes = extractCommandScopes(texFile, scope.commands);
    } else if (scope.environments && scope.environments.length > 0) {
        outerScopes = extractEnvironmentScopes(texFile, scope.environments);
    } else if (scope.wholeDocument) {
        outerScopes = extractDocumentScope(texFile);
    } else {
        outerScopes = [{ text: texFile, absStart: 0, absEnd: texFile.length }];
    }

    if (scope.innerCommand) {
        outerScopes = extractInnerCommandScopes(outerScopes, scope.innerCommand);
    }

    if (onlyEnvironments.length > 0) {
        outerScopes = filterToOnlyEnvironments(texFile, outerScopes, onlyEnvironments);
    }
    if (onlyMath) {
        outerScopes = filterToOnlyMathZones(texFile, outerScopes, onlyMath === true ? true : onlyMath);
    }



    const allEnvExclusions = [...excludeEnvironments, ...excludeOnlyEnvironments];

    const exclusionZones = [];
    if (!onlyMath) {
        getMathZones(texFile, excludeMath).forEach(z => exclusionZones.push(z));
    }
    if (excludeInner.length > 0)
        buildCommandExclusionZones(texFile, excludeInner).forEach(z => exclusionZones.push(z));
    if (allEnvExclusions.length > 0)
        buildEnvExclusionZones(texFile, allEnvExclusions).forEach(z => exclusionZones.push(z));
    if (excludeAllCommands)
        buildAllCommandExclusionZones(texFile).forEach(z => exclusionZones.push(z));

    exclusionZones.sort((a, b) => a.start - b.start);

    // ── Step 4: Split → clean segments ───────────────────────────────────────
    const rawSegments = [];
    for (const outer of outerScopes)
        splitByExclusions(outer, exclusionZones).forEach(s => rawSegments.push(s));

    // ── Step 5: Build emitters ────────────────────────────────────────────────
    const suggestionEmitter = makeAnchorEmitter({
        texFile, session, Range, anchorlist,
        existingSuggestions, staleIds,
        styleName, markerClass, overlapMode,
    });

    const queryEmitter = buildQueryOnlyEmitter({
        texFile, session, anchorlist,
        existingSuggestions, staleIds,
        styleName, markerClass, insertedAQSet,
    });

    // ── Step 6: Attach helpers to each segment ────────────────────────────────
    const segments = rawSegments.map(seg => ({
        text: seg.text,
        absStart: seg.absStart,

        emit(originalText, suggestionText, localOffset, description, extraStyleInfo = {}) {
            suggestionEmitter(originalText, suggestionText, seg.absStart + localOffset, description, extraStyleInfo);
        },

        emitQuery(originalText, queryMessage, localOffset, description) {
            queryEmitter(originalText, queryMessage, seg.absStart + localOffset, description);
        },
    }));

    // ── Step 7: Top-level emitters (absolute index) ───────────────────────────
    return {
        segments,

        emitQuery(originalText, queryMessage, absIndex, description) {
            queryEmitter(originalText, queryMessage, absIndex, description);
        },

        emitSuggestion(originalText, suggestionText, absIndex, description, extraStyleInfo = {}) {
            suggestionEmitter(originalText, suggestionText, absIndex, description, extraStyleInfo);
        },
    };
}

